import { useState } from "react";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import { Stars } from "../../components/common/Badges";
import ReasonDialog from "../../components/admin/ReasonDialog";
import { ABUSE_REASON, ABUSE_STATUS, fmtDate } from "../../lib/labels";
import { MODERATION_ACTION, usePlatformCan } from "../../lib/platform";

const History = ({ fid, onClose }) => {
  const { data, loading, error } = useApi(`/admin/moderation/feedbacks/${fid}/history`);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="admin-moderation-history-dialog">
        <DialogHeader><DialogTitle>Historique de modération</DialogTitle></DialogHeader>
        {loading && <Loader />}
        {error && <ErrorState message={error} />}
        {data && (
          <div className="space-y-4 text-sm">
            <p className="text-xs text-slate-500">État actuel : {data.feedback.moderation_hidden ? "masqué publiquement" : "visible selon ses règles de publication"}. Le contenu et la note de l'avis ne sont jamais modifiés.</p>
            <div><p className="eyebrow mb-2">Décisions</p>
              {!data.actions.length && <p className="text-xs text-slate-500">Aucune décision.</p>}
              <ul className="space-y-2">{data.actions.map((a) => <li key={a.id} className="rounded-md border p-2 text-xs" data-testid="admin-moderation-action"><b>{MODERATION_ACTION[a.action] || a.action}</b> · {a.actor_name} · {fmtDate(a.created_at)}<p className="mt-0.5 text-slate-600">Motif : {a.reason}</p></li>)}</ul>
            </div>
            <div><p className="eyebrow mb-2">Signalements ({data.reports.length})</p>
              <ul className="space-y-1 text-xs">{data.reports.map((r) => <li key={r.id}>{fmtDate(r.created_at)} · {ABUSE_REASON[r.reason]} · {ABUSE_STATUS[r.status]?.label}{r.details ? ` · « ${r.details} »` : ""}</li>)}</ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Report = ({ r, can, onDecide, onRestore, onHistory }) => (
  <div className="card-surface p-5" data-testid={`admin-abuse-${r.id}`}>
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className={`rounded-full border px-2 py-0.5 text-xs ${ABUSE_STATUS[r.status]?.cls}`}>{ABUSE_STATUS[r.status]?.label}</span>
      <b>{ABUSE_REASON[r.reason]}</b><span className="text-slate-500">· {r.company_name} · {r.tracking_code} · signalé par {r.source === "public" ? "un visiteur" : r.reporter_name}</span>
      <span className="ml-auto text-xs text-slate-400">{fmtDate(r.created_at)}</span>
    </div>
    {r.details && <p className="mt-2 text-sm italic text-slate-600">« {r.details} »</p>}
    {r.feedback && (
      <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
        <Stars value={r.feedback.rating} />{r.feedback.title && <p className="mt-1 font-semibold">{r.feedback.title}</p>}
        <p className="mt-1 whitespace-pre-wrap text-slate-700">{r.feedback.message}</p>
        {r.feedback.moderation_hidden && <p className="mt-2 text-xs font-medium text-rose-700" data-testid={`admin-abuse-hidden-${r.id}`}>Actuellement masqué publiquement</p>}
      </div>
    )}
    {r.decision_note && <p className="mt-2 text-xs text-slate-500">Décision de {r.decided_by} ({fmtDate(r.decided_at)}) : {r.decision_note}</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      {can("moderation:decide") && r.status === "pending" && <>
        <Button size="sm" variant="destructive" onClick={() => onDecide(r, "upheld")} data-testid={`admin-abuse-uphold-${r.id}`}>Retenir (masquer publiquement)</Button>
        <Button size="sm" variant="outline" onClick={() => onDecide(r, "dismissed")} data-testid={`admin-abuse-dismiss-${r.id}`}>Rejeter le signalement</Button>
      </>}
      {can("moderation:decide") && r.feedback?.moderation_hidden && r.status !== "pending" && <Button size="sm" variant="outline" onClick={() => onRestore(r)} data-testid={`admin-abuse-restore-${r.id}`}>Lever la restriction</Button>}
      <Button size="sm" variant="ghost" onClick={() => onHistory(r.feedback_id)} data-testid={`admin-abuse-history-${r.id}`}>Historique</Button>
    </div>
  </div>
);

export default function AdminAbuse() {
  const can = usePlatformCan();
  const [status, setStatus] = useState("pending");
  const [pending, setPending] = useState(null);
  const [history, setHistory] = useState(null);
  const { data, loading, error, reload } = useApi(`/admin/abuse-reports?status=${status}`);
  const confirm = async (reason) => {
    try {
      if (pending.decision === "restore") await api.post(`/admin/moderation/feedbacks/${pending.r.feedback_id}/restore`, { reason });
      else await api.post(`/admin/abuse-reports/${pending.r.id}`, { decision: pending.decision, note: reason });
      toast.success("Décision enregistrée"); setPending(null); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const TITLES = { upheld: "Retenir le signalement", dismissed: "Rejeter le signalement", restore: "Lever la restriction" };
  return (
    <div>
      <PageHeader title="Modération" description="Avis signalés : motif, décision, auteur et historique. Aucune note ni aucun commentaire n'est modifié ; toute restriction est justifiée et auditée."
        actions={<select className="field-select w-44" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-abuse-status-filter">
          <option value="pending">En examen</option><option value="upheld">Retenus</option><option value="dismissed">Rejetés</option><option value="">Tous</option>
        </select>} />
      {loading && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.length && <EmptyState icon={ShieldAlert} title="Aucun signalement" />}
      <div className="space-y-3">{data?.map((r) => <Report key={r.id} r={r} can={can} onDecide={(rep, decision) => setPending({ r: rep, decision })}
        onRestore={(rep) => setPending({ r: rep, decision: "restore" })} onHistory={setHistory} />)}</div>
      {pending && <ReasonDialog testId="admin-abuse-decision" title={TITLES[pending.decision]}
        label="Confirmer" variant={pending.decision === "upheld" ? "destructive" : "default"} placeholder="Justification de la décision (tracée dans l'historique et le journal d'audit)"
        onConfirm={confirm} onClose={() => setPending(null)} />}
      {history && <History fid={history} onClose={() => setHistory(null)} />}
    </div>
  );
}
